import { notificationPreferencesOptions } from "@repo/client/data/notifications";
import { trainingConfigOptions } from "@repo/client/data/profile";
import {
  type NotificationPreferences,
  type NotificationPreferencesOutput,
  notificationPreferences,
} from "@repo/contract/notifications";
import {
  "profile-editors.reminder_days_error" as daysError,
  "profile-editors.error_field" as errorField,
  "profile-editors.eyebrow_notifications" as eyebrowNotifications,
  "profile-editors.reminder_title" as reminderTitle,
} from "@repo/i18n";
import { useApi } from "../../../data/api";
import {
  type EditorHost,
  EditorShell,
  useSaveFlow,
  useSectionRead,
} from "./editor-shell";
import { ReminderForm } from "./reminder-form";
import {
  deviceUtcOffset,
  type ReminderForm as ReminderDraft,
  reminderFormFrom,
  reminderPayload,
} from "./reminder-model";
import { useEditorForm } from "./use-editor-form";
import { useNotificationPreferencesMutation } from "./use-editor-mutation";
import { hasErrorAt } from "./validate";

const NO_DAYS: readonly number[] = [];

/**
 * The daily reminder: `PUT /me/notification-preferences`, the whole shape.
 * The hour is picked LOCAL and stored in UTC with the device's offset beside
 * it (`reminder-model.ts`); the two notification toggles ride along as read.
 */
export function ReminderEditor({ host }: { readonly host: EditorHost }) {
  const api = useApi();
  const read = useSectionRead(notificationPreferencesOptions(api));
  const config = useSectionRead(trainingConfigOptions(api));
  const flow = useSaveFlow(useNotificationPreferencesMutation(), host);
  const trainingDays = config.data?.fixedWeekdays ?? NO_DAYS;

  const form = useEditorForm({
    onSubmit: (payload: NotificationPreferences) => flow.save(payload),
    schema: notificationPreferences,
    // Read once the training days are known, so a stored list equal to them
    // opens as "on training days".
    server: config.loading ? null : read.data,
    toDraft: (prefs: NotificationPreferencesOutput | null) =>
      reminderFormFrom(
        prefs ?? {
          reminderUtcMinute: null,
          reminderWeekdays: null,
          utcOffsetMinutes: 0,
        },
        trainingDays
      ),
    toPayload: (draft: ReminderDraft) =>
      reminderPayload(
        read.data ?? { sorenessEnabled: true, summaryEnabled: true },
        draft,
        // The offset NOW, sent beside the minute it converted.
        deviceUtcOffset(new Date()),
        trainingDays
      ),
  });

  const weekdaysWrong = hasErrorAt(form.errors, "reminderWeekdays");
  const otherWrong = !weekdaysWrong && Object.keys(form.errors).length > 0;
  return (
    <EditorShell
      eyebrow={eyebrowNotifications()}
      failure={flow.failure ?? (otherWrong ? errorField() : null)}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading || config.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={reminderTitle()}
    >
      <ReminderForm
        daysError={weekdaysWrong ? daysError() : undefined}
        onChange={form.setDraft}
        trainingDays={trainingDays}
        value={form.draft}
      />
    </EditorShell>
  );
}
